// store/useVideoToImagesStore.js
import { create } from "zustand";

export const DEFAULT_OPTIONS = {
  samplingMode: "fps",   // fps | nth | count
  fps: 1,                // sensible default per the dataset-prep spec
  nthFrame: 30,
  totalCount: 100,
  outputFormat: "jpeg",
  quality: 90,
  resizeMode: "keep",    // keep | max
  maxWidth: 1920,
  maxHeight: 1080,
};

const useVideoToImagesStore = create((set) => ({
  // ---- engine / capability info ----
  engine: null,
  supportedExtensions: [],
  setEngineInfo: (value) => set({
    engine: value?.engine || null,
    supportedExtensions: value?.supportedExtensions || [],
  }),

  // ---- destination ----
  // 'model'   -> straight into a project model version (labelable immediately)
  // 'dataset' -> a standalone named dataset, transferred to a model later
  targetMode: "model",
  setTargetMode: (value) => set({ targetMode: value }),

  datasetName: "",
  setDatasetName: (value) => set({ datasetName: value }),

  projects: [],
  setProjects: (value) => set({ projects: value }),

  models: [],
  setModels: (value) => set({ models: value }),

  selectedProjectId: "",
  setSelectedProjectId: (value) => set({ selectedProjectId: value, selectedModelId: "" }),

  selectedModelId: "",
  setSelectedModelId: (value) => set({ selectedModelId: value }),

  // ---- source files ----
  // Local File objects the user picked, before upload.
  selectedFiles: [],
  setSelectedFiles: (value) => set({ selectedFiles: value }),
  addSelectedFiles: (files) => set((state) => {
    const seen = new Set(state.selectedFiles.map((f) => `${f.relativePath}:${f.file.size}`));
    const additions = files.filter((f) => !seen.has(`${f.relativePath}:${f.file.size}`));
    return { selectedFiles: [...state.selectedFiles, ...additions] };
  }),
  removeSelectedFile: (relativePath) => set((state) => ({
    selectedFiles: state.selectedFiles.filter((f) => f.relativePath !== relativePath),
  })),
  clearSelectedFiles: () => set({ selectedFiles: [] }),

  dragOver: false,
  setDragOver: (value) => set({ dragOver: value }),

  // ---- upload progress ----
  isUploading: false,
  uploadPercent: 0,
  uploadedBytes: 0,
  setUploadState: (isUploading, uploadPercent = 0, uploadedBytes = 0) =>
    set({ isUploading, uploadPercent, uploadedBytes }),

  // Result of /upload_sources — what start_extraction needs.
  stagedBatch: null,
  setStagedBatch: (value) => set({ stagedBatch: value }),

  // ---- options ----
  options: { ...DEFAULT_OPTIONS },
  setOption: (key, value) => set((state) => ({ options: { ...state.options, [key]: value } })),
  resetOptions: () => set({ options: { ...DEFAULT_OPTIONS } }),

  // ---- jobs ----
  jobs: [],
  setJobs: (value) => set({ jobs: value }),
  jobsError: null,
  setJobsError: (value) => set({ jobsError: value }),

  isStarting: false,
  setIsStarting: (value) => set({ isStarting: value }),

  detailJob: null,
  setDetailJob: (value) => set({ detailJob: value }),
  detailLogs: [],
  setDetailLogs: (value) => set({ detailLogs: value }),

  // ---- datasets (holding area) ----
  datasets: [],
  setDatasets: (value) => set({ datasets: value }),
  datasetsError: null,
  setDatasetsError: (value) => set({ datasetsError: value }),

  // The dataset currently opened in the browser panel, plus its loaded page.
  activeDataset: null,
  setActiveDataset: (value) => set({ activeDataset: value }),
  datasetItems: [],
  setDatasetItems: (value) => set({ datasetItems: value }),
  appendDatasetItems: (value) =>
    set((state) => ({ datasetItems: [...state.datasetItems, ...value] })),
  datasetTotal: 0,
  setDatasetTotal: (value) => set({ datasetTotal: value }),
  isDatasetLoading: false,
  setIsDatasetLoading: (value) => set({ isDatasetLoading: value }),

  // Multi-select. A Set of dataset-item ids.
  selectedItemIds: new Set(),
  toggleItemSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedItemIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedItemIds: next };
    }),
  setSelectedItemIds: (ids) => set({ selectedItemIds: new Set(ids) }),
  clearItemSelection: () => set({ selectedItemIds: new Set() }),

  // ---- creating a destination inline ----
  // Which picker, if either, has been switched into "new" mode. The name lives
  // beside it so a half-typed name survives a re-render.
  creatingProject: false,
  setCreatingProject: (value) => set({ creatingProject: value, newProjectName: '' }),
  newProjectName: '',
  setNewProjectName: (value) => set({ newProjectName: value }),
  creatingModel: false,
  setCreatingModel: (value) => set({ creatingModel: value, newModelName: '' }),
  newModelName: '',
  setNewModelName: (value) => set({ newModelName: value }),
  newModelType: 'classification',
  setNewModelType: (value) => set({ newModelType: value }),
  isCreatingTarget: false,
  setIsCreatingTarget: (value) => set({ isCreatingTarget: value }),

  isDownloading: false,
  setIsDownloading: (value) => set({ isDownloading: value }),

  // ---- preparing a dataset for labeling (the AI build) ----
  isBuildOpen: false,
  setIsBuildOpen: (value) => set({ isBuildOpen: value }),
  buildTask: 'classification',            // classification | bounding_box | unlabeled
  setBuildTask: (value) => set({ buildTask: value }),
  buildClassMode: 'auto',                 // auto | fixed
  setBuildClassMode: (value) => set({ buildClassMode: value }),
  buildClassCount: 3,
  setBuildClassCount: (value) => set({ buildClassCount: value }),
  buildClassNames: '',
  setBuildClassNames: (value) => set({ buildClassNames: value }),
  buildObjects: '',
  setBuildObjects: (value) => set({ buildObjects: value }),
  buildConfidence: 0.25,
  setBuildConfidence: (value) => set({ buildConfidence: value }),
  // Set when the build modal is labeling an EXISTING model version in place
  // (a video extraction that went straight into a model) rather than copying a
  // dataset selection. Null means the normal dataset flow.
  versionTarget: null,
  setVersionTarget: (value) => set({ versionTarget: value }),
  versionUnlabeled: null,
  setVersionUnlabeled: (value) => set({ versionUnlabeled: value }),

  isStartingBuild: false,
  setIsStartingBuild: (value) => set({ isStartingBuild: value }),

  // Builds for the open dataset, newest first. Polled while one is running.
  builds: [],
  setBuilds: (value) => set({ builds: value }),

  // ---- transfer to a model ----
  transferProjectId: "",
  setTransferProjectId: (value) =>
    set({ transferProjectId: value, transferModelId: "", transferVersionId: "", modelVersions: [] }),
  transferModelId: "",
  setTransferModelId: (value) => set({ transferModelId: value, transferVersionId: "" }),
  transferVersionId: "",
  setTransferVersionId: (value) => set({ transferVersionId: value }),
  transferModels: [],
  setTransferModels: (value) => set({ transferModels: value }),
  modelVersions: [],
  setModelVersions: (value) => set({ modelVersions: value }),

  resetAll: () => set({
    selectedFiles: [],
    stagedBatch: null,
    isUploading: false,
    uploadPercent: 0,
    uploadedBytes: 0,
    options: { ...DEFAULT_OPTIONS },
    detailJob: null,
    detailLogs: [],
    datasetName: "",
    selectedItemIds: new Set(),
    creatingProject: false,
    creatingModel: false,
    newProjectName: '',
    newModelName: '',
  }),
}));

export default useVideoToImagesStore;
